import { notification } from "antd";
import i18n from "i18next";
import { resolveApiErrorMessage } from "./apiErrorMessage";

/**
 * Dashboard widgets (hot terms, search stats, top topics, topic ratings,
 * decision distribution, driver analysis) answer HTTP 422 with this code when
 * the selected range covers more reviews than one request may load (80,000 by
 * default; pivony-api review_load_guard.py). The body also carries
 * `review_count` and `max_reviews`.
 */
export const REVIEW_RANGE_TOO_LARGE = "REVIEW_RANGE_TOO_LARGE";

// One notice for the whole page: every widget of a too-large range gets the
// same 422, and antd replaces a notification that has the same key.
const NOTICE_KEY = "review-range-too-large";

export const isReviewRangeTooLarge = (result) =>
  Boolean(
    result &&
      typeof result === "object" &&
      result.error_code === REVIEW_RANGE_TOO_LARGE
  );

const formatCount = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return String(value);
  try {
    return number.toLocaleString(i18n.language || undefined);
  } catch (e) {
    return String(number);
  }
};

export const reviewRangeTooLargeText = (result, t) => {
  const title = t("review_range_too_large.title", "Too many reviews for one view");
  const hasCounts = result?.review_count != null && result?.max_reviews != null;
  if (!hasCounts && result?.error) {
    // Counts missing: the API's own message (English or Turkish) says the same.
    return { title, description: String(result.error) };
  }
  const description = t(
    "review_range_too_large.description",
    "This view covers {{reviews}} reviews, more than the {{limit}} a chart can load at once. Choose a shorter date range or add a filter.",
    {
      reviews: formatCount(result?.review_count),
      limit: formatCount(result?.max_reviews),
    }
  );
  return { title, description };
};

/** Shows (or refreshes) the page's single "range too large" notice. */
export const notifyReviewRangeTooLarge = (result, t) => {
  const { title, description } = reviewRangeTooLargeText(result, t);
  notification.warning({
    key: NOTICE_KEY,
    message: title,
    description,
    // Stays until the user changes the range (closeReviewRangeNotice) or closes it.
    duration: 0,
  });
};

/** Call when a new widget load starts, so a notice about the old range goes away. */
export const closeReviewRangeNotice = () => notification.destroy(NOTICE_KEY);

/**
 * The error toast dashboard widgets show when their request fails, except that
 * a too-large range shows the single range notice instead of one red error per
 * widget.
 */
export const notifyWidgetError = (result, t, fallbackKey) => {
  if (isReviewRangeTooLarge(result)) {
    notifyReviewRangeTooLarge(result, t);
    return;
  }
  notification.error({
    message: resolveApiErrorMessage(result, t, fallbackKey),
  });
};
